import { useState, useEffect } from 'react';
import { useHelpTips } from '../context/HelpTipsContext';

const ARROW_SIZE = 7;
const BG = '#1a1f2e';

function getBubblePosition(position, animated) {
  // offset slides in from the opposite direction on mount
  const dy = animated ? 0 : 5;
  switch (position) {
    case 'top':    return { bottom: 'calc(100% + 10px)', left: 16, transform: `translateY(${dy}px)` };
    case 'bottom': return { top:    'calc(100% + 10px)', left: 16, transform: `translateY(${-dy}px)` };
    case 'right':  return { left:   'calc(100% + 10px)', top: '50%', transform: `translateY(-50%) translateX(${-dy}px)` };
    case 'left':   return { right:  'calc(100% + 10px)', top: '50%', transform: `translateY(-50%) translateX(${dy}px)` };
    default:       return { top:    'calc(100% + 10px)', left: 16, transform: `translateY(${-dy}px)` };
  }
}

function getArrowStyle(position) {
  const base = { position: 'absolute', width: 0, height: 0, pointerEvents: 'none' };
  const t = BG;
  switch (position) {
    case 'bottom':
      return { ...base, bottom: '100%', left: 16,
               borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`,
               borderBottom: `${ARROW_SIZE}px solid ${t}` };
    case 'top':
      return { ...base, top: '100%', left: 16,
               borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`,
               borderTop: `${ARROW_SIZE}px solid ${t}` };
    case 'right':
      return { ...base, right: '100%', top: '50%', transform: 'translateY(-50%)',
               borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`,
               borderRight: `${ARROW_SIZE}px solid ${t}` };
    case 'left':
      return { ...base, left: '100%', top: '50%', transform: 'translateY(-50%)',
               borderTop: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE}px solid transparent`,
               borderLeft: `${ARROW_SIZE}px solid ${t}` };
    default:
      return { ...base, bottom: '100%', left: 16,
               borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`,
               borderBottom: `${ARROW_SIZE}px solid ${t}` };
  }
}

// Help bubbles remain disabled. Keep the parked implementation isolated so its
// hooks can be linted without introducing a conditional early return.
export default function HelpTip() { return null; }

function HelpTipPreview({ id, position = 'bottom', children, style: customStyle }) {
  const { isVisible, dismissTip } = useHelpTips();
  const visible = isVisible(id);
  const [animated, setAnimated] = useState(false);

  // Trigger entrance animation one tick after mount
  useEffect(() => {
    if (!visible) { setAnimated(false); return; }
    const t = setTimeout(() => setAnimated(true), 30);
    return () => clearTimeout(t);
  }, [visible]);

  if (!visible) return null;

  const positionStyle = getBubblePosition(position, animated);

  const bubbleStyle = {
    position: 'absolute',
    zIndex: 600,
    background: BG,
    color: '#f5f3ef',
    border: '1px solid #d4a84a',
    borderRadius: 8,
    padding: '10px 28px 10px 14px',
    fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
    fontSize: 'calc(13px * var(--text-scale, 1))',
    maxWidth: 260,
    boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
    lineHeight: 1.45,
    whiteSpace: 'normal',
    pointerEvents: 'auto',
    opacity: animated ? 1 : 0,
    transition: 'opacity 0.25s ease, transform 0.25s ease',
    ...positionStyle,
    ...customStyle,
  };

  const closeStyle = {
    position: 'absolute',
    top: 4,
    right: 6,
    background: 'none',
    border: 'none',
    color: '#aaa09a',
    fontSize: 16,
    cursor: 'pointer',
    padding: '2px 4px',
    lineHeight: 1,
    fontFamily: 'inherit',
  };

  return (
    <div style={bubbleStyle} role="tooltip" aria-live="polite">
      {children}
      <button style={closeStyle} onClick={() => dismissTip(id)} aria-label="Dismiss tip">×</button>
      <div style={getArrowStyle(position)} aria-hidden="true" />
    </div>
  );
}
